import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MessageCircle, Phone, Mail, Users, Send, MousePointerClick,
  Eye, Ban, TrendingUp, Activity, Loader2, Plus, ChevronRight, Briefcase,
  Globe2, Settings2, ExternalLink, Sparkles, Megaphone,
} from 'lucide-react';
import ModuleDemoVideo from '../components/ModuleDemoVideo';

// Static class maps so Tailwind JIT picks them up
const TONE_KPI = {
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/20', text: 'text-violet-300' },
  sky: { bg: 'bg-sky-500/10', border: 'border-sky-500/20', text: 'text-sky-300' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-300' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-300' },
  rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/20', text: 'text-rose-300' },
  zinc: { bg: 'bg-zinc-500/10', border: 'border-zinc-500/20', text: 'text-zinc-300' },
};

const TONE_CHANNEL = {
  violet: {
    bg: 'from-violet-500/5', border: 'border-violet-500/20', borderHover: 'hover:border-violet-500/40',
    iconBg: 'bg-violet-500/15', iconBorder: 'border-violet-500/30', iconText: 'text-violet-300',
    link: 'text-violet-300 hover:text-violet-200',
    btn: 'bg-violet-500/15 hover:bg-violet-500/25 border-violet-500/40 text-violet-200',
  },
  emerald: {
    bg: 'from-emerald-500/5', border: 'border-emerald-500/20', borderHover: 'hover:border-emerald-500/40',
    iconBg: 'bg-emerald-500/15', iconBorder: 'border-emerald-500/30', iconText: 'text-emerald-300',
    link: 'text-emerald-300 hover:text-emerald-200',
    btn: 'bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-500/40 text-emerald-200',
  },
  green: {
    bg: 'from-green-500/5', border: 'border-green-500/20', borderHover: 'hover:border-green-500/40',
    iconBg: 'bg-green-500/15', iconBorder: 'border-green-500/30', iconText: 'text-green-300',
    link: 'text-green-300 hover:text-green-200',
    btn: 'bg-green-500/15 hover:bg-green-500/25 border-green-500/40 text-green-200',
  },
};

const CHANNEL_BADGE = {
  email: { cls: 'bg-violet-500/15 border-violet-500/30 text-violet-200', label: 'Email' },
  sms: { cls: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200', label: 'SMS' },
  whatsapp: { cls: 'bg-green-500/15 border-green-500/30 text-green-200', label: 'WhatsApp' },
};

const KpiCard = ({ icon: Icon, label, value, sub, tone = 'violet', testId }) => {
  const t = TONE_KPI[tone] || TONE_KPI.violet;
  return (
    <div
      data-testid={testId}
      className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${t.bg} border ${t.border}`}>
          <Icon className={`w-4 h-4 ${t.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold text-white leading-none">{value}</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{label}</p>
          {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

const ChannelCard = ({ tone, title, description, sent, extra, icon: Icon, onOpen, onCompose, composeLabel, testId, statusNote, links = [] }) => {
  const t = TONE_CHANNEL[tone] || TONE_CHANNEL.violet;
  return (
    <div
      data-testid={testId}
      className={`relative bg-gradient-to-br ${t.bg} to-zinc-900 border ${t.border} ${t.borderHover} rounded-2xl p-5 transition-colors`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${t.iconBg} border ${t.iconBorder} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${t.iconText}`} />
          </div>
          <div>
            <h3 className="text-base font-semibold">{title}</h3>
            <p className="text-[11px] text-zinc-500">{description}</p>
          </div>
        </div>
        <button
          onClick={onOpen}
          className={`text-[11px] ${t.link} inline-flex items-center gap-0.5`}
          data-testid={`${testId}-open`}
        >
          Open <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-2xl font-bold">{sent.toLocaleString()}</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Lifetime sent</p>
        </div>
        <div>
          <p className="text-2xl font-bold">{extra.value.toLocaleString()}</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">{extra.label}</p>
        </div>
      </div>
      {links.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`${testId}-links`}>
          {links.map((link) => (
            <button
              key={link.label}
              type="button"
              onClick={link.onClick}
              className={`px-2 py-1 rounded-md text-[10px] border ${t.border} ${t.link} bg-zinc-950/40 hover:bg-zinc-900/80`}
            >
              {link.label}
            </button>
          ))}
        </div>
      )}
      {statusNote && (
        <p className="text-[10px] text-emerald-400 mb-2" data-testid={`${testId}-status`}>{statusNote}</p>
      )}
      <button
        onClick={onCompose}
        data-testid={`${testId}-compose`}
        className={`w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-medium border ${t.btn}`}
      >
        <Plus className="w-3.5 h-3.5" /> {composeLabel}
      </button>
    </div>
  );
};

const ChannelBadge = ({ channel }) => {
  const m = CHANNEL_BADGE[channel] || { cls: 'bg-zinc-500/15 border-zinc-500/30 text-zinc-200', label: channel };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${m.cls}`}>
      {m.label}
    </span>
  );
};

const ConfigBadge = ({ ready, label }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${
      ready
        ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200'
        : 'bg-zinc-500/10 border-zinc-600 text-zinc-400'
    }`}
  >
    {ready ? `${label} ready` : `${label} not set up`}
  </span>
);

const WebsiteMarketingCard = ({ site, navigate }) => {
  const email = site.email || {};
  const sms = site.sms || {};
  const openEmail = () => navigate(`/marketing-suite/email?website=${encodeURIComponent(site.id)}`);
  const openSms = () => navigate(`/marketing-suite/sms?website=${encodeURIComponent(site.id)}`);
  const openSiteMarketing = () => navigate(`/marketing/${site.id}`);

  return (
    <div
      data-testid={`ms-website-${site.id}`}
      className="text-left bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 hover:border-violet-500/30 transition-colors"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center shrink-0">
            <Globe2 className="w-5 h-5 text-violet-300" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold truncate">{site.name}</h3>
            <p className="text-[11px] text-zinc-500 truncate">
              {site.genre_id ? `${site.genre_id} · ` : ''}{site.slug || site.id.slice(0, 8)}
            </p>
          </div>
        </div>
        {site.has_activity ? (
          <span className="text-[10px] uppercase tracking-wider text-emerald-400 shrink-0">Active</span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 shrink-0">No sends yet</span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
        <div className="rounded-lg bg-zinc-950/50 border border-zinc-800 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Email</p>
          <p className="text-lg font-bold text-white">{(email.sent || 0).toLocaleString()}</p>
          <p className="text-zinc-500">{email.campaigns || 0} campaigns · {email.subscribers_active || 0} subs</p>
          <div className="mt-2">
            <ConfigBadge ready={email.configured} label="Email" />
          </div>
        </div>
        <div className="rounded-lg bg-zinc-950/50 border border-zinc-800 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">SMS</p>
          <p className="text-lg font-bold text-white">{(sms.sent || 0).toLocaleString()}</p>
          <p className="text-zinc-500">{sms.campaigns || 0} campaigns · {sms.contacts || 0} contacts</p>
          <div className="mt-2">
            <ConfigBadge ready={sms.configured} label="SMS" />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={openEmail}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] border border-violet-500/30 text-violet-200 hover:bg-violet-500/10"
        >
          <Mail className="w-3 h-3" /> Email
        </button>
        <button
          type="button"
          onClick={openSms}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10"
        >
          <Phone className="w-3 h-3" /> SMS
        </button>
        <button
          type="button"
          onClick={openSiteMarketing}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        >
          <ExternalLink className="w-3 h-3" /> Site panel
        </button>
      </div>
    </div>
  );
};

const MarketingSuiteDashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [websiteSnapshots, setWebsiteSnapshots] = useState([]);

  const token = localStorage.getItem('token');
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        setLoadError(null);
        const [sumRes, wsRes] = await Promise.all([
          fetch(`${API}/api/marketing-suite/summary`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/marketing-suite/websites`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (sumRes.ok) {
          setSummary(await sumRes.json());
        } else {
          setLoadError('Could not load marketing summary');
        }
        if (wsRes.ok) {
          const wsData = await wsRes.json();
          setWebsiteSnapshots(wsData.websites || []);
        }
      } catch (e) {
        console.error('Failed to load marketing summary', e);
        setLoadError('Could not load marketing summary');
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [API, token]);

  const channelRoute = (channel, tab = '') => {
    const base = channel === 'whatsapp' ? '/marketing-suite/whatsapp' : `/marketing-suite/${channel}`;
    if (!tab) return base;
    const qs = new URLSearchParams({ tab });
    if (tab === 'campaigns') qs.set('new', '1');
    return `${base}?${qs.toString()}`;
  };

  const renderRecent = () => {
    if (!summary || !summary.recent_activity || summary.recent_activity.length === 0) {
      return (
        <div className="text-center text-zinc-500 text-xs py-10" data-testid="ms-recent-empty">
          <Activity className="w-6 h-6 mx-auto mb-2 opacity-40" />
          No campaign activity yet. Launch your first campaign to populate this feed.
        </div>
      );
    }
    return (
      <ul className="divide-y divide-zinc-800/60" data-testid="ms-recent-list">
        {summary.recent_activity.map((r, idx) => (
          <li key={`${r.channel}-${r.id || idx}`} className="py-2 flex items-center gap-3">
            <ChannelBadge channel={r.channel} />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white truncate">{r.name}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">
                {r.website_name ? `${r.website_name} · ` : ''}{r.status}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-emerald-300">{(r.sent || 0).toLocaleString()} sent</p>
              {r.opens != null && <p className="text-[10px] text-zinc-500">{r.opens} opens</p>}
              {r.recipients != null && r.opens == null && (
                <p className="text-[10px] text-zinc-500">{r.recipients} recipients</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  const c = summary?.contacts || {};
  const e = summary?.email || {};
  const s = summary?.sms || {};
  const w = summary?.whatsapp || {};

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-10 bg-zinc-950 text-white">
      <header className="flex items-center justify-between mb-8" data-testid="ms-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold">Marketing Suite</h1>
            <p className="text-zinc-400 text-sm">
              Unified Email, SMS, WhatsApp & CRM — powered by Snagcart.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/lead-scraper')}
            data-testid="ms-lead-scraper-btn"
            className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-medium border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-white"
          >
            <Briefcase className="w-3.5 h-3.5" /> Lead Scraper
          </button>
          <button
            onClick={() => navigate('/marketing-suite/seo')}
            data-testid="ms-seo-btn"
            className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-medium border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/15"
          >
            <Sparkles className="w-3.5 h-3.5" /> AI SEO
          </button>
          <button
            onClick={() => navigate('/marketing-suite/ads')}
            data-testid="ms-ads-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-medium border border-amber-500/40 text-amber-200 hover:bg-amber-500/15"
          >
            <Megaphone className="w-3.5 h-3.5" /> AI Ads
          </button>
          <button
            onClick={() => navigate('/marketing-suite/crm')}
            data-testid="ms-crm-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-medium bg-violet-500/15 border border-violet-500/40 text-violet-200 hover:bg-violet-500/25"
          >
            <Users className="w-3.5 h-3.5" /> Open CRM
          </button>
        </div>
      </header>

      <ModuleDemoVideo moduleKey="marketing-suite" className="mb-6" />

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400 text-sm" data-testid="ms-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Marketing Suite…
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200" data-testid="ms-error">
          {loadError}. Refresh the page or sign in again.
        </div>
      ) : (
        <>
          <section
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8"
            data-testid="ms-kpi-grid"
          >
            <KpiCard testId="ms-kpi-contacts" icon={Users} tone="violet" label="All contacts"
              value={(c.all_contacts || 0).toLocaleString()} sub={`${c.crm_contacts || 0} CRM · ${c.subscribers_active || 0} subs`} />
            <KpiCard testId="ms-kpi-leads" icon={Briefcase} tone="sky" label="CRM Leads"
              value={(c.crm_leads || 0).toLocaleString()} sub="Pipeline-ready" />
            <KpiCard testId="ms-kpi-sent" icon={Send} tone="emerald" label="Emails sent"
              value={(e.sent || 0).toLocaleString()} sub={`${e.campaigns || 0} campaigns`} />
            <KpiCard testId="ms-kpi-open" icon={Eye} tone="amber" label="Open rate"
              value={`${(e.open_rate || 0).toFixed(1)}%`} sub={`${e.opens || 0} opens`} />
            <KpiCard testId="ms-kpi-click" icon={MousePointerClick} tone="rose" label="Click rate"
              value={`${(e.click_rate || 0).toFixed(1)}%`} sub={`${e.clicks || 0} clicks`} />
            <KpiCard testId="ms-kpi-blacklist" icon={Ban} tone="zinc" label="Suppression list"
              value={((e.blacklist || 0) + (s.blacklist || 0)).toLocaleString()} sub="Email + SMS opt-outs" />
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ms-channel-grid">
              <ChannelCard
                testId="ms-channel-email"
                tone="violet"
                title="Email Marketing"
                description="Campaigns, lists, templates, analytics"
                icon={Mail}
                sent={e.sent || 0}
                extra={{ label: 'Campaigns', value: e.campaigns || 0 }}
                links={[
                  { label: `Lists (${e.lists || 0})`, onClick: () => navigate(channelRoute('email', 'lists')) },
                  { label: `Templates (${e.templates || 0})`, onClick: () => navigate(channelRoute('email', 'templates')) },
                  { label: 'Analytics', onClick: () => navigate(channelRoute('email', 'analytics')) },
                ]}
                onOpen={() => navigate(channelRoute('email'))}
                onCompose={() => navigate(channelRoute('email', 'campaigns'))}
                composeLabel="Compose email"
              />
              <ChannelCard
                testId="ms-channel-sms"
                tone="emerald"
                title="SMS Marketing"
                description="Twilio/Textlocal SMS & MMS"
                icon={Phone}
                sent={s.sent || 0}
                extra={{ label: 'Recipients', value: s.recipients || 0 }}
                links={[
                  { label: `Lists (${s.lists || 0})`, onClick: () => navigate(channelRoute('sms', 'contacts')) },
                  { label: `Templates (${s.templates || 0})`, onClick: () => navigate(channelRoute('sms', 'templates')) },
                  { label: 'Analytics', onClick: () => navigate(channelRoute('sms', 'analytics')) },
                ]}
                onOpen={() => navigate(channelRoute('sms'))}
                onCompose={() => navigate(channelRoute('sms', 'campaigns'))}
                composeLabel="Compose SMS"
              />
              <ChannelCard
                testId="ms-channel-whatsapp"
                tone="green"
                title="WhatsApp Marketing"
                description="Broadcasts, chatbots, tickets"
                icon={MessageCircle}
                sent={w.sent || 0}
                extra={{ label: 'Channels', value: w.channels || 0 }}
                statusNote={w.connected ? 'Channel connected' : (w.channels ? 'Channel ready — connect WhatsApp' : null)}
                links={[
                  { label: `Broadcasts (${w.broadcasts || 0})`, onClick: () => navigate(channelRoute('whatsapp', 'broadcasts')) },
                  { label: `Chatbots (${w.chatbots || 0})`, onClick: () => navigate(channelRoute('whatsapp', 'chatbots')) },
                  { label: `Tickets (${w.tickets || 0})`, onClick: () => navigate(channelRoute('whatsapp', 'tickets')) },
                ]}
                onOpen={() => navigate(channelRoute('whatsapp'))}
                onCompose={() => navigate(channelRoute('whatsapp', 'broadcasts'))}
                composeLabel="New broadcast"
              />
            </div>

            <aside className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="ms-recent">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold inline-flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" /> Recent activity
                </h3>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Last {summary?.recent_activity?.length || 0} events
                </span>
              </div>
              {renderRecent()}
            </aside>
          </div>

          <section className="mb-8" data-testid="ms-websites-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold inline-flex items-center gap-2">
                  <Settings2 className="w-5 h-5 text-violet-400" />
                  Marketing by website
                </h2>
                <p className="text-xs text-zinc-500 mt-1">
                  Each site has its own email/SMS workspace, provider settings, lists, and campaigns.
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                {websiteSnapshots.length} site{websiteSnapshots.length === 1 ? '' : 's'}
              </span>
            </div>
            {websiteSnapshots.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-zinc-700 bg-zinc-900/40 p-8 text-center text-sm text-zinc-500">
                <Globe2 className="w-8 h-8 mx-auto mb-3 opacity-40" />
                No websites yet. Create a site from My Websites, then return here to configure email and SMS per site.
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-medium bg-violet-500/15 border border-violet-500/40 text-violet-200 hover:bg-violet-500/25"
                  >
                    Go to My Websites
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {websiteSnapshots.map((site) => (
                  <WebsiteMarketingCard key={site.id} site={site} navigate={navigate} />
                ))}
              </div>
            )}
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/marketing-suite/seo')}
              data-testid="ms-seo-card"
              className="text-left bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 hover:border-emerald-500/40 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-emerald-300" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">AI SEO Agent</h3>
                  <p className="text-[11px] text-zinc-500">Crawl · Audit · Apply fixes</p>
                </div>
              </div>
              <p className="text-xs text-zinc-400">
                Paste any URL. Audit, plan, and apply safe SEO fixes to your Snagcart sites.
              </p>
            </button>
            <button
              onClick={() => navigate('/marketing-suite/ads')}
              data-testid="ms-ads-card"
              className="text-left bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 hover:border-amber-500/40 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">AI Ads Agent</h3>
                  <p className="text-[11px] text-zinc-500">Social calendar · Paid kits</p>
                </div>
              </div>
              <p className="text-xs text-zinc-400">
                Generate headlines, primary text, keywords, and a launch checklist — no live spend yet.
              </p>
            </button>
            <button
              onClick={() => navigate('/marketing-suite/crm')}
              data-testid="ms-crm-card"
              className="text-left bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 hover:border-violet-500/40 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                  <Users className="w-5 h-5 text-violet-300" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">Contacts & CRM</h3>
                  <p className="text-[11px] text-zinc-500">Leads · Contacts · Deals · Kanban Pipeline</p>
                </div>
              </div>
              <p className="text-xs text-zinc-400">
                {(c.crm_contacts || 0)} contacts · {(c.crm_leads || 0)} leads in pipeline.
                Convert leads to opportunities, drag through stages, export to CSV.
              </p>
            </button>
            <button
              onClick={() => navigate('/lead-scraper')}
              data-testid="ms-leadscraper-card"
              className="text-left bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 hover:border-emerald-500/40 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-emerald-300" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">Lead Scraper</h3>
                  <p className="text-[11px] text-zinc-500">Find prospects · Push to CRM · Source-tracked</p>
                </div>
              </div>
              <p className="text-xs text-zinc-400">
                Search Google Places-style queries, save the results to your CRM with the
                <span className="text-emerald-300"> lead_scraper</span> source tag.
              </p>
            </button>
          </section>
        </>
      )}
    </div>
  );
};

export default MarketingSuiteDashboard;
